<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { useQueryGetAsrJobs } from '@/js/services/api/backOffice/backOfficeQueries';
    import type { AsrJobStatus } from '@/js/services/api/backOffice/asrEnqueueApi';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import { RefreshCwIcon } from '@lucide/vue';

    const props = defineProps<{
        tenantId: string;
        heading: string;
    }>();

    const {
        data: jobs,
        isLoading: isLoadingJobs,
        isFetching: isFetchingJobs,
        refetch: refetchJobs,
    } = useQueryGetAsrJobs(() => props.tenantId);

    defineExpose({ refetch: refetchJobs });

    const statusDisplay = computed<Record<AsrJobStatus, { label: string; color: string }>>(
        () => ({
            Queued: {
                label: __('back-office-job-table-status-queued'),
                color: 'text-gray-medium',
            },
            Running: { label: __('back-office-job-table-status-running'), color: 'text-blue-500' },
            Succeeded: {
                label: __('back-office-job-table-status-succeeded'),
                color: 'text-success',
            },
            Failed: { label: __('back-office-job-table-status-failed'), color: 'text-error' },
        })
    );

    function formatDate(date: string | null): string {
        return date ? new Date(date).toLocaleString() : '-';
    }
</script>
<template>
    <div>
        <div class="flex items-center gap-2 mb-2">
            <h2 class="heading-md">{{ heading }}</h2>
            <EchoTooltip :label="__('back-office-job-table-refresh')">
                <RefreshCwIcon
                    :class="[
                        'w-5 h-5 cursor-pointer hover:text-gray-medium transition-colors',
                        { 'animate-spin': isFetchingJobs },
                    ]"
                    @click="refetchJobs()"
                />
            </EchoTooltip>
        </div>
        <p class="text-caption text-gray-medium mb-1">
            {{ __('back-office-job-table-count', { count: String(jobs?.length ?? 0) }) }}
        </p>
        <div class="overflow-x-auto">
            <table class="w-full border-collapse">
                <thead>
                    <tr class="border-b border-gray-lightest">
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-status') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-interaction-id') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-priority') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-worker') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-created') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-started') }}
                        </th>
                        <th class="py-3 pr-6 text-left text-caption-bold uppercase">
                            {{ __('back-office-job-table-col-ended') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isLoadingJobs">
                        <td colspan="7" class="py-12 text-center"><LoadingDots /></td>
                    </tr>
                    <template v-else>
                        <tr v-if="!jobs?.length">
                            <td colspan="7" class="py-6 text-body text-gray-medium text-center">
                                {{ __('back-office-job-table-empty') }}
                            </td>
                        </tr>
                        <tr
                            v-for="job in jobs"
                            :key="job.id"
                            class="border-b border-gray-lightest"
                            :title="job.output || undefined"
                        >
                            <td
                                class="py-3 pr-6 text-left text-body"
                                :class="statusDisplay[job.status].color"
                            >
                                {{ statusDisplay[job.status].label }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ job.interactionPartExternalId }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.priority }}</td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.workerId || '-' }}</td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.createdAt) }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.startedAt) }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.endedAt) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>
